"use client";

import { useState, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import { cn } from "@/utils";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import Link from "next/link";
import { ContentImageLightbox } from "@/components/ContentImageLightbox";

interface MarkdownContentProps {
  content: string;
  className?: string;
}

function CodeBlock({ children, className, ...props }: any) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    // Extract text content from children
    const codeContent =
      typeof children === "string" ? children : children?.props?.children || "";

    if (codeContent) {
      try {
        await navigator.clipboard.writeText(codeContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="code-copy-button"
        onClick={copyToClipboard}
        type="button"
      >
        {copied ? (
          <>
            <Check className="size-4 mr-1 text-secondary-500" />
            Copied!
          </>
        ) : (
          <>Copy to Clipboard</>
        )}
      </Button>
      <pre className={className} {...props}>
        {children}
      </pre>
    </div>
  );
}

// Extract text content from React children recursively
function extractTextContent(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (!children) return "";

  if (Array.isArray(children)) {
    return children.map(extractTextContent).join("");
  }

  if (typeof children === "object" && "props" in children) {
    return extractTextContent((children as any).props?.children);
  }

  return "";
}

// Custom table wrapper for horizontal scrolling
function TableWrapper({ children, ...props }: any) {
  return (
    <div className="markdown-table-wrapper overflow-x-auto w-full">
      <table {...props}>{children}</table>
    </div>
  );
}

// Custom table cell with line-clamp and tooltip
function TableCell({ children, ...props }: any) {
  const textContent = extractTextContent(children);
  const needsTooltip = textContent.length > 50; // Show tooltip for longer content

  return (
    <td {...props}>
      {needsTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="line-clamp-2 cursor-help">{children}</div>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-sm whitespace-normal break-words"
          >
            {textContent}
          </TooltipContent>
        </Tooltip>
      ) : (
        <div className="line-clamp-2">{children}</div>
      )}
    </td>
  );
}

// Custom strong component to link "BINARLY REsearch team" to the research page
function StrongText({ children, ...props }: any) {
  const textContent = extractTextContent(children);

  // Check if this is the BINARLY REsearch team text (handles both "REsearch" and "Research")
  if (
    textContent.toLowerCase().includes("research team") ||
    textContent.toLowerCase().includes("binarly team") ||
    textContent.toLowerCase().includes("binarly efixplorer team")
  ) {
    return (
      <strong {...props}>
        <Link
          href="/research#research-team"
          className="text-primary-200 hover:text-primary-100 transition-colors font-normal"
        >
          {children}
        </Link>
      </strong>
    );
  }

  return <strong {...props}>{children}</strong>;
}

export function MarkdownInline({
  content,
  className = "",
}: {
  content: string;
  className?: string;
}) {
  if (!content) return null;

  return (
    <span className={cn("markdown markdown-inline-title", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <>{children}</>,
        }}
      >
        {content}
      </ReactMarkdown>
    </span>
  );
}

export default function MarkdownContent({
  content,
  className = "",
  ...props
}: MarkdownContentProps) {
  const [imageLightbox, setImageLightbox] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  return (
    <div className={cn("markdown ", className)} {...props}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeHighlight]}
        components={{
          pre: CodeBlock,
          table: TableWrapper,
          td: TableCell,
          strong: StrongText,
          img: ({ node: _node, ...props }: any) => (
            <img
              {...props}
              className={cn(props.className, "cursor-zoom-in")}
              onClick={(e) => {
                props.onClick?.(e);
                if (props.src) {
                  setImageLightbox({
                    src: String(props.src),
                    alt: String(props.alt ?? ""),
                  });
                }
              }}
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
      <ContentImageLightbox
        open={imageLightbox !== null}
        onOpenChange={(open) => {
          if (!open) setImageLightbox(null);
        }}
        src={imageLightbox?.src ?? ""}
        alt={imageLightbox?.alt ?? ""}
      />
    </div>
  );
}
